import { useTranslation } from 'next-i18next'

import { Link } from '~/components/Link'
import { IconName } from '~/components/Icons/types'
import { TUTOR_PANEL_VIEW } from '~/types/tutor'
import { STUDENT_PANEL_VIEW } from '~/types/student'
import { ADMIN_PANEL_VIEW } from '~/types/admin'
import SVGImage from '~/components/Image/SVGImage'
import HomCalendarIcon from '~/components/Icons/svgs/HomCalendarIcon'
import HomCameraCircleIcon from '~/components/Icons/svgs/HomCameraCircleIcon'
import HomChecklistIcon from '~/components/Icons/svgs/HomChecklistIcon'
import HomClassroomAltIcon from '~/components/Icons/svgs/HomClassroomAltIcon'
import HomClassroomIcon from '~/components/Icons/svgs/HomClassroomIcon'
import HomClockIcon from '~/components/Icons/svgs/HomClockIcon'
import HomGraduationCapCircleIcon from '~/components/Icons/svgs/HomGraduationCapCircleIcon'
import HomLaptopIcon from '~/components/Icons/svgs/HomLaptopIcon'
import HomPersonWithTabletIcon from '~/components/Icons/svgs/HomPersonWithTabletIcon'
import HomReceiptIcon from '~/components/Icons/svgs/HomReceiptIcon'
import HomSettingsIcon from '~/components/Icons/svgs/HomSettingsIcon'
import MentorChatIcon from '~/components/Icons/svgs/MentorChatIcon'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'

export default function PanelMobileMenu<T extends ADMIN_PANEL_VIEW | TUTOR_PANEL_VIEW | STUDENT_PANEL_VIEW>({
  disabledTabs,
  tabs,
}: {
  disabledTabs?: T[]
  tabs: Array<{ href: string; icon?: IconName; type: T }>
}): React.ReactElement {
  const { t } = useTranslation('generics')
  return (
    <div className="relative">
      <SVGImage
        alt={t('bean')}
        imageClassName="absolute z-0 top-32 opacity-25 transform scale-110"
        srcKey="mentor-on-demand/bean-v2.svg"
      />
      <nav className="relative z-10 space-y-3">
        {tabs.map(({ href, icon, type }) => (
          <PanelMobileMenuTab
            href={href}
            icon={icon}
            isDisabled={Boolean(disabledTabs?.includes(type))}
            key={type}
            type={type}
          />
        ))}
      </nav>
    </div>
  )
}

function PanelMobileMenuTab<T extends ADMIN_PANEL_VIEW | TUTOR_PANEL_VIEW | STUDENT_PANEL_VIEW>({
  href,
  icon,
  isDisabled,
  type,
}: {
  href: string
  icon?: IconName
  isDisabled: boolean
  type: T
}): React.ReactElement {
  const { t } = useTranslation('panels')
  const classNames = isDisabled ? 'pointer-events-none' : ''

  const getIconComponent = (name: string): ((props: { [key: string]: any }) => React.ReactElement) => {
    switch (name) {
      case 'hom-calendar':
        return HomCalendarIcon
      case 'hom-graduation-cap-circle':
        return HomGraduationCapCircleIcon
      case 'hom-receipt':
        return HomReceiptIcon
      case 'hom-camera-circle':
        return HomCameraCircleIcon
      case 'mentor-chat':
        return MentorChatIcon
      case 'hom-settings':
        return HomSettingsIcon
      case 'hom-classroom-alt':
        return HomClassroomAltIcon
      case 'hom-classroom':
        return HomClassroomIcon
      case 'hom-person-with-tablet':
        return HomPersonWithTabletIcon
      case 'hom-laptop':
        return HomLaptopIcon
      case 'hom-checklist':
        return HomChecklistIcon
      case 'hom-clock':
        return HomClockIcon
      default:
        return HomClockIcon
    }
  }

  return (
    <Link className={`p-4 flex items-center rounded shadow-large bg-white ${classNames}`} to={href}>
      <span className="flex-1 flex items-start transform scale-105">
        {icon && <SVGIcon icon={getIconComponent(icon)} size={26} />}
      </span>
      <span className={`font-semibold ${isDisabled ? 'text-black text-opacity-25' : 'text-hotpink'}`}>
        {t(`tab.${type}`)}
      </span>
      <span className="flex-1" />
    </Link>
  )
}
